/**
 * Domínio: eventos que só o navegador conhece — GUPM-335.
 *
 * O servidor sabe quando uma sessão nasce ou morre (trigger em `auth.sessions`).
 * O que nunca chega ao banco sozinho — o erro que a Microsoft devolveu, o clique
 * em "Sair", a tela negada, o arquivo exportado — o app
 * registra pela RPC `registrar_evento_app`, que tem a MESMA allowlist no banco.
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

/** Ações aceitas pela RPC. Espelha `v_anonimas || v_logadas` da migration. */
export type AcaoEventoApp =
  | 'login_iniciado'
  | 'login_falha_provedor'
  | 'logout'
  | 'acesso_negado'
  | 'exportacao_dados';

/** Dados aceitos por ação — o banco descarta qualquer outra chave. */
export interface DadosEventoApp {
  login_iniciado: { provedor: 'azure' };
  login_falha_provedor: FalhaOAuth & { provedor?: string };
  logout: { origem: OrigemLogout };
  acesso_negado: { rota: string; perfil: string | null; perfis_exigidos: string[] };
  exportacao_dados: { tela: string; arquivo: string; linhas: number; filtro?: string };
}

/** De onde partiu o "Sair". */
export type OrigemLogout = 'menu' | 'tela_sem_acesso';

/** Erro de login devolvido na URL de retorno (Entra ID ou GoTrue). */
export interface FalhaOAuth {
  erro: string;
  erro_codigo?: string;
  erro_descricao?: string;
}

/**
 * Mascara e-mail em texto livre. A Microsoft põe o e-mail da conta em algumas
 * descrições (ex.: AADSTS50020); e-mail vindo do navegador não é identidade
 * provada e não fica na trilha. O banco repete a máscara.
 */
export function mascararEmails(texto: string): string {
  return texto.replace(/[^\s@]+@[^\s@]+/g, '[e-mail omitido]');
}

/**
 * Lê o erro de login da URL de retorno, se houver.
 *
 * O fluxo implícito devolve no fragmento (`#error=…`); o PKCE e os erros do
 * próprio GoTrue (ex.: trigger de signup recusando), na query (`?error=…`). Os
 * dois são lidos. URL de sucesso (`#access_token=…`) ou sem erro → `null`.
 * Nunca lança: a tela de login não pode cair por causa de auditoria.
 */
export function extrairFalhaOAuth(href: string): FalhaOAuth | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  for (const params of [
    new URLSearchParams(url.hash.replace(/^#/, '')),
    url.searchParams,
  ]) {
    const erro = params.get('error');
    if (!erro) continue;
    const falha: FalhaOAuth = { erro };
    const codigo = params.get('error_code');
    const descricao = params.get('error_description');
    if (codigo) falha.erro_codigo = codigo;
    if (descricao) falha.erro_descricao = mascararEmails(descricao);
    return falha;
  }
  return null;
}
